import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePenalExecution } from '../../context/penalExecutionContext';
import { useInternals } from '../../context/internalContext';
import Modal from '../../components/modal';
import { toast } from 'react-toastify';
import useSubmitLock from '../../utils/useSubmitLock';

// Estado inicial do formulário
const initialFormData = (internalId) => ({
  nu: '',
  internal: internalId || '',
  current_regime: '',
  years: '',
  months: '',
  days: '',
  start_date: '',
  base_date: '',
  progression_date: '',
  expected_release_date: '',
  detraction_days: '',
  is_active: true,
});

function PenalExecution({ internalId, executionId, isOpen, closeModal, onSaveSuccess, readOnly }) {
  const {
    addPenalExecution,
    updatePenalExecution,
    getPenalExecutionById,
  } = usePenalExecution();

  const {
    listInternalRegime,
  } = useInternals();

  const [formData, setFormData] = useState(initialFormData(internalId));
  const [loadingRegimes, setLoadingRegimes] = useState(false);
  const [regimes, setRegimes] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const originalExecutionRef = useRef(null);
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const isEditMode = !!executionId;

  //  Limpa o formulário ao abrir o modal para novo cadastro
  useEffect(() => {
    if (!isEditMode && isOpen) {
      setFormData(initialFormData(internalId));
    }
  }, [isOpen, isEditMode, internalId]);

  //  Busca dados da execução penal para edição
  useEffect(() => {
    let isMounted = true;

    async function fetchExecutionData() {
      try {
        setIsLoadingData(true);
        const execution = await getPenalExecutionById(executionId);
        if (isMounted) {
          originalExecutionRef.current = {
            nu: execution.nu || '',
            current_regime: execution.current_regime || '',
            years: execution.years || '',
            months: execution.months || '',
            days: execution.days || '',
            start_date: execution.start_date ? execution.start_date.split('T')[0] : '',
            base_date: execution.base_date ? execution.base_date.split('T')[0] : '',
            progression_date: execution.progression_date ? execution.progression_date.split('T')[0] : '',
            expected_release_date: execution.expected_release_date ? execution.expected_release_date.split('T')[0] : '',
            detraction_days: execution.detraction_days || '',
            is_active: execution.is_active ?? true,
          };
          setFormData({
            nu: execution.nu || '',
            internal: execution.internal || internalId || '',
            current_regime: execution.current_regime || '',
            years: execution.years || '',
            months: execution.months || '',
            days: execution.days || '',
            start_date: execution.start_date ? execution.start_date.split('T')[0] : '',
            base_date: execution.base_date ? execution.base_date.split('T')[0] : '',
            progression_date: execution.progression_date ? execution.progression_date.split('T')[0] : '',
            expected_release_date: execution.expected_release_date ? execution.expected_release_date.split('T')[0] : '',
            detraction_days: execution.detraction_days || '',
            is_active: execution.is_active ?? true,
          });
        }
      } catch (error) {
        console.error("Erro ao buscar execução penal:", error);
      } finally {
        if (isMounted) {
          setIsLoadingData(false);
        }
      }
    }

    if (isEditMode) {
      fetchExecutionData();
    } else {
      setFormData(initialFormData(internalId));
      setIsLoadingData(false);
    }

    return () => { isMounted = false; };
  }, [isEditMode, executionId, getPenalExecutionById, internalId]);


  useEffect(() => {
    let isMounted = true;

    async function fetchRegimes() {
      try {
        setLoadingRegimes(true);
        const response = await listInternalRegime(internalId);

        if (!isMounted) return;

        setRegimes(
          Array.isArray(response?.results)
            ? response.results
            : (Array.isArray(response) ? response : [])
        );
      } catch (error) {
        if (!isMounted) return;
        setRegimes([]);
      } finally {
        if (isMounted) {
          setLoadingRegimes(false);
        }
      }
    }

    fetchRegimes();

    return () => { isMounted = false; };
  }, [listInternalRegime, internalId]);

  //  Handler para campos do formulário
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  //  Handler de submit do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      const payloadJson = {
        nu: formData.nu || '',
        internal: formData.internal || internalId || '',
        current_regime: formData.current_regime || '',
        years: formData.years || '',
        months: formData.months || '',
        days: formData.days || '',
        is_active: formData.is_active,
      };

      const optionalDateFields = ['start_date', 'base_date', 'progression_date', 'expected_release_date'];
      optionalDateFields.forEach((field) => {
        if (formData[field]) payloadJson[field] = formData[field];
      });
      if (formData.detraction_days !== '') payloadJson.detraction_days = formData.detraction_days;

      const computeChangedPayload = () => {
        if (!isEditMode || !originalExecutionRef.current) return payloadJson;
        const original = originalExecutionRef.current;
        const changed = {};

        if ((payloadJson.nu || '') !== (original.nu || '')) changed.nu = payloadJson.nu;
        if ((payloadJson.current_regime || '') !== (original.current_regime || '')) changed.current_regime = payloadJson.current_regime;
        if ((payloadJson.years || '') !== (original.years || '')) changed.years = payloadJson.years;
        if ((payloadJson.months || '') !== (original.months || '')) changed.months = payloadJson.months;
        if ((payloadJson.days || '') !== (original.days || '')) changed.days = payloadJson.days;
        if ((payloadJson.start_date || '') !== (original.start_date || '')) changed.start_date = payloadJson.start_date;
        if ((payloadJson.base_date || '') !== (original.base_date || '')) changed.base_date = payloadJson.base_date;
        if ((payloadJson.progression_date || '') !== (original.progression_date || '')) changed.progression_date = payloadJson.progression_date;
        if ((payloadJson.expected_release_date || '') !== (original.expected_release_date || '')) changed.expected_release_date = payloadJson.expected_release_date;
        if ((payloadJson.detraction_days || '') !== (original.detraction_days || '')) changed.detraction_days = payloadJson.detraction_days;
        if (payloadJson.is_active !== (original.is_active ?? true)) changed.is_active = payloadJson.is_active;
        return changed;
      };

      let payloadToSend = payloadJson;
      if (isEditMode) {
        const changedPayload = computeChangedPayload();
        payloadToSend = changedPayload;
      }

      try {
        if (isEditMode) {
          // O método no context deve realizar um PATCH
          await updatePenalExecution(executionId, payloadToSend);
        } else {
          await addPenalExecution(payloadToSend);
        }
        setFormData(initialFormData(internalId));
        onSaveSuccess();
        closeModal();
      } catch (error) {
        console.error("Erro ao salvar execução penal:", error);
        let apiMessage = 'Erro ao processar solicitação';
        if (error.response?.data) {
          const data = error.response.data;
          if (data.message) {
            apiMessage = data.message;
          } else if (data.detail) {
            apiMessage = data.detail;
          } else if (typeof data === 'object') {
            // Captura erros de validação por campo (ex: {"start_date": ["Data inválida"]})
            const messages = Object.entries(data).map(([key, val]) => {
              const fieldName = key.replace('_', ' ').toUpperCase();
              const message = Array.isArray(val) ? val.join(', ') : val;
              return `${fieldName}: ${message}`;
            });
            if (messages.length > 0) apiMessage = messages.join(' | ');
          }
        }
        toast.error(apiMessage);
      }
    });
  };

  return (
    <Modal
      title={readOnly ? 'Visualizar Execução Penal' : isEditMode ? 'Editar Execução Penal' : 'Adicionar Execução Penal'}
      isOpen={isOpen}
      closeModal={closeModal}
    >
      {isLoadingData ? (
        <div className="empty-state-message"><p>Carregando dados da execução...</p></div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className='form-group'>
            <label>Nº Execução Penal</label>
            <input
              type="text"
              name="nu"
              value={formData.nu || ''}
              onChange={(e) => {
                const onlyNumbers = e.target.value.replace(/\D/g, '');
                handleChange({
                  target: {
                    name: 'nu',
                    value: onlyNumbers
                  }
                });
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              required
              disabled={readOnly}
            />
          </div>
          <div className='form-group'>
            <label>Regime Atual</label>
            <select
              name="current_regime"
              value={String(formData.current_regime || '')}
              onChange={handleChange}
              required
              disabled={readOnly || loadingRegimes}
            >
              <option value="">Selecione</option>
              {regimes.map((regime) => (
                <option key={regime.id} value={regime.id}>
                  {regime.name || regime.description || regime.regime || regime.title}
                </option>
              ))}
            </select>
          </div>

          <div className='form-group'>
            <h3>Pena Total</h3>
            <div className="form-row">
              <div className="form-field-small">
                <label>Anos</label>
                <input
                  type="number"
                  name="years"
                  value={formData.years || ''}
                  onChange={handleChange}
                  required
                  disabled={readOnly}
                />
              </div>
              <div className="form-field-small">
                <label>Meses</label>
                <input
                  type="number"
                  name="months"
                  value={formData.months || ''}
                  onChange={handleChange}
                  required
                  disabled={readOnly}
                />
              </div>
              <div className="form-field-small">
                <label>Dias</label>
                <input
                  type="number"
                  name="days"
                  value={formData.days || ''}
                  onChange={handleChange}
                  required
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className='form-group form-field-half'>
              <label>Data de Início</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date || ''}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            <div className='form-group form-field-half'>
              <label>Data Base</label>
              <input
                type="date"
                name="base_date"
                value={formData.base_date || ''}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="form-row">
            <div className='form-group form-field-half'>
              <label>Previsão de Progressão</label>
              <input
                type="date"
                name="progression_date"
                value={formData.progression_date || ''}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            <div className='form-group form-field-half'>
              <label>Data Prevista de Liberação</label>
              <input
                type="date"
                name="expected_release_date"
                value={formData.expected_release_date || ''}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>
          </div>

          <div className='form-group'>
            <label>Dias Remidos</label>
            <input
              type="number"
              name="detraction_days"
              value={formData.detraction_days || ''}
              onChange={handleChange}
              disabled={readOnly}
            />
          </div>

          <div className='form-group'>
            <label>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active || false}
                onChange={handleChange}
                disabled={readOnly}
              />
              Ativo
            </label>
          </div>

          {!readOnly && (
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : isEditMode ? 'Salvar Alterações' : 'Adicionar Execução Penal'}
            </button>
          )}
        </form>
      )}
    </Modal>
  );
}

export default PenalExecution;
